// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Imports
// ----------------------------------------------------------------------------
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Libraries
import React from 'react'
import compose from 'recompose/compose'
import { connect } from 'react-redux'

import map from 'lodash/map'
import replace from 'lodash/replace'
import find from 'lodash/find'
import isUndefined from 'lodash/isUndefined'
import split from 'lodash/split'
import join from 'lodash/join'
import slice from 'lodash/slice'

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Components
import { StaticQuery, graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import { navigate } from 'gatsby-plugin-react-intl'

import Row from 'antd/lib/row'
import 'antd/lib/row/style/css'

import Col from 'antd/lib/col'
import 'antd/lib/col/style/css'

import Collapse from 'antd/lib/collapse'
import 'antd/lib/collapse/style/css'

import Tree from 'antd/lib/tree'
import 'antd/lib/tree/style/css'

import { HierarchicalMenu, RefinementList } from 'react-instantsearch-dom'

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Locals
import Glider from './glider'
import './glider/style.less'

import Link from '../link'
import '../link/style.less'

import { updateHeaderState } from '../../state/actions'

import categories from '../../data/categories.json'
import categoriesKeysAndSlugs from '../../data/categories-keys-and-slugs.json'
import keywords from '../../data/keywords.json'
import years from '../../data/years.json'
import decades from '../../data/decades.json'
import authors from '../../data/authors.json'
import publishers from '../../data/publishers.json'

import length from '../../methods/length'

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Abstractions
const { Fragment } = React
const { Panel } = Collapse

export const query = graphql`
  query {
    s1: file(relativePath: { eq: "symbols/s4blu.jpg" }) {
      childImageSharp {
        gatsbyImageData(
          width: 900
          placeholder: TRACED_SVG
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
    s2: file(relativePath: { eq: "symbols/s5blu.jpg" }) {
      childImageSharp {
        gatsbyImageData(
          width: 900
          placeholder: TRACED_SVG
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
    s3: file(relativePath: { eq: "symbols/s6blu.jpg" }) {
      childImageSharp {
        gatsbyImageData(
          width: 900
          placeholder: TRACED_SVG
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
  }
`

// ----------------------------------------------------------------------------
// ------------------------------------------------------------------ Component
// ----------------------------------------------------------------------------
const DesktopHeader = React.memo(
  ({
    pageSchema: { breadcrumbs = { title: 'Homepage', slug: '' } },
    headerState,
    updateHeaderState,
    location,
  }) => {
    const { pathname } = location
    const path = replace(pathname, '/en/', '/')
    const expandedTreeKey = find(categoriesKeysAndSlugs, [
      'slug',
      join(slice(split(path, '/'), 0, length(split(path, '/')) - 1), '/'),
    ])

    return (
      <Fragment>
        <div>
          <StaticQuery
            query={query}
            render={({ s1, s2, s3 }) => (
              <nav>
                <li>
                  <Link
                    to="#"
                    className="action"
                    onClick={(e) => {
                      e.preventDefault()
                      updateHeaderState({
                        isActive: !headerState.isActive,
                      })
                    }}
                  >
                    &nbsp;
                  </Link>
                </li>
              </nav>
            )}
          />
          <Link to="/" label="Open-Access Archive" title="Open-Access Archive">
            <h1>Open-Access Archive</h1>
          </Link>
        </div>
        <div>
          <Link className="as-collapse-header" to="/">
            <span>Homepage</span>
          </Link>
          <Collapse
            defaultActiveKey={[split(path, '/')[1]]}
            bordered={false}
            onChange={(key) => {
              const whatsActive = key[1]
              const whatsAlreadyActive = [split(path, '/')[1]]
              if (whatsActive !== whatsAlreadyActive) {
                if (whatsActive === 'search') {
                  console.debug('must navigate to search')
                  navigate('/search')
                }
              }
            }}
          >
            <Panel
              header={<span>Categories</span>}
              key="categories"
              showArrow={false}
            >
              <Tree
                treeData={categories}
                showLine
                titleRender={({ slug, title, totalObjects }) => (
                  <Link to={`${slug}/page-1`}>
                    <span className="title">{title}&nbsp;</span>
                    <span className="objects">{totalObjects}</span>
                  </Link>
                )}
                defaultExpandedKeys={
                  isUndefined(expandedTreeKey) === false && [
                    expandedTreeKey.key,
                  ]
                }
              />
            </Panel>
            <Panel
              header={<span>Keywords</span>}
              key="keywords"
              showArrow={false}
            >
              <ul>
                {map(keywords, ({ title, totalObjects, slug }) => (
                  <li>
                    <Link to={`${slug}/page-1`}>
                      <span className="title">{title}&nbsp;</span>
                      <span className="count">{totalObjects}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel
              header={<span>Decades</span>}
              key="decades"
              showArrow={false}
            >
              <ul>
                {map(decades, ({ title, totalObjects, slug }) => (
                  <li>
                    <Link to={`${slug}/page-1`}>
                      <span className="title">{title}&nbsp;</span>
                      <span className="count">{totalObjects}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel header={<span>Years</span>} key="years" showArrow={false}>
              <ul>
                {map(years, ({ title, totalObjects, slug }) => (
                  <li>
                    <Link to={`${slug}/page-1`}>
                      <span className="title">{title}&nbsp;</span>
                      <span className="count">{totalObjects}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel
              header={<span>Authors</span>}
              key="authors"
              showArrow={false}
            >
              <ul>
                {map(authors, ({ title, totalObjects, slug }) => (
                  <li>
                    <Link to={`${slug}/page-1`}>
                      <span className="title">{title}&nbsp;</span>
                      <span className="count">{totalObjects}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel
              header={<span>Publishers</span>}
              key="publishers"
              showArrow={false}
            >
              <ul>
                {map(publishers, ({ title, totalObjects, slug }) => (
                  <li>
                    <Link to={`${slug}/page-1`}>
                      <span className="title">{title}&nbsp;</span>
                      <span className="count">{totalObjects}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          </Collapse>
          <Link className="as-collapse-header" to="/search">
            <span>Search</span>
            <div>
              <h3 className="unset">Publisher</h3>
              <RefinementList attribute="publisher" />
              <h3>Category</h3>
              <HierarchicalMenu
                attributes={[
                  'categories.lvl0',
                  'categories.lvl1',
                  'categories.lvl2',
                  'categories.lvl3',
                ]}
              />
              <h3>Decade</h3>
              <RefinementList attribute="decade" />
              <h3>Year</h3>
              <RefinementList attribute="year" />
              <h3>Authors</h3>
              <RefinementList attribute="authors" />
            </div>
          </Link>
          <Link className="as-collapse-header" to="/contact">
            <span>Contact</span>
          </Link>
        </div>
        <Glider>
          <Row>
            <Col xs={0} sm={0} md={0} lg={16} xl={16} xxl={16}>
              <h1>Open-Access Archive</h1>
              <p className="c tj">
                Welcome! The Auroville Open-Access Archive is a free
                distribution service and an open-access archive designed to
                preserve and provide open access to images, articles, audio,
                video and other media, and/or digital data relating to
                Auroville.
              </p>
              <p className="c tj">
                A paragraph about the main project. Take this from Kristen's
                editorial.
              </p>
              <p className="c tj">A paragraph summarising the archive.</p>
              <p className="c tj">
                A paragraph about how users can explore it.
              </p>
              <p className="c tj">
                <strong>Categories:</strong> a paragraph about categorisation.
              </p>
              <p className="c tj">
                <strong>Keywords:</strong> a paragraph about keywords.
              </p>
              <p className="c tj">
                <strong>Years:</strong> a paragraph about years.
              </p>
              <p className="c tj">
                <strong>Decades:</strong> a paragraph about decades.
              </p>
              <p className="c tj">
                A paragraph about the search feature. Also link to the search.
              </p>
            </Col>
            <Col xs={0} sm={0} md={0} lg={8} xl={8} xxl={8}>
              &nbsp;
            </Col>
          </Row>
        </Glider>
      </Fragment>
    )
  }
)

// ----------------------------------------------------------------------------
// ---------------------------------------------------------------------- State
// ----------------------------------------------------------------------------
const withState = connect(
  (state) => ({
    headerState: state.headerState,
  }),
  (dispatch) => ({
    updateHeaderState(payload) {
      dispatch(updateHeaderState(payload))
    },
  })
)

// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Compose
// ----------------------------------------------------------------------------
/** Compose ala FP style */
const ComposedDesktopHeader = compose(
  withState // Add state
)(DesktopHeader)

// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Exports
// ----------------------------------------------------------------------------
export default ComposedDesktopHeader
